import { Button, EchartsWithTheme, Empty, Modal, useTheme } from '@maxtropy/components';
import { apiV2EnergyReportTrendPost } from '@maxtropy/device-customer-apis-v2';
import dayjs, { Dayjs } from 'dayjs';
import React, { useContext, useMemo, useState } from 'react';
import { DateType } from '../../const';
import { buildMonthCycleParams } from '../../utils';
import styles from './index.module.scss';
import { useRequest } from 'ahooks';
import { getGasCostTrendChartOption, getChartOption, CommonGasChartConfig } from './charts';
import CardBox from '../CardBox';
import { GasType, gasTypeToNumber, useGasData } from './useGetGasData';
import { MonthPeriodContext } from '../..';

interface GasChartProps {
  gasType: GasType;
  showTitle: boolean;
  timeResolution: DateType;
  dateValue?: [Dayjs | null, Dayjs | null] | null;
}

const gasConfigsForChart: Record<GasType, CommonGasChartConfig> = {
  [GasType.GAS]: {
    title: '压缩空气',
    unit: 'm³',
    color: '#00ADFF',
    showProportion: true,
    proportionColor: '#FFCB47',
    modalLegendName: '冷风外付',
  },
  [GasType.OXYGEN]: { title: '液氧', unit: 't', color: '#FFCB47 ', modalLegendName: '液氧耗费' },
  [GasType.CH4]: { title: '天然气', unit: 'm³', color: '#16DD8E ', modalLegendName: '燃气耗费' },
  [GasType.CO2]: { title: '二氧化碳', unit: 't', color: '#854ECA', modalLegendName: '二氧化碳耗费' },
  [GasType.ARGON]: { title: '液氩', unit: 't', color: '#00ADFF', modalLegendName: '液氩耗费' },
};

const GasChart: React.FC<GasChartProps> = ({ gasType, showTitle, timeResolution, dateValue }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const trendType = gasTypeToNumber[gasType];
  const { reportScope, monthPeriodType, monthPeriodStart, monthPeriodEnd, monthCycleApiParams } =
    useContext(MonthPeriodContext);

  const gasResult = useGasData(
    gasType,
    timeResolution,
    dateValue ?? null,
    monthPeriodType,
    monthPeriodStart,
    monthPeriodEnd,
    reportScope,
    monthCycleApiParams
  );

  const chartOption = useMemo(() => {
    const config = gasConfigsForChart[gasType];
    return getChartOption(config, gasResult.data, timeResolution);
  }, [gasResult.data, timeResolution, gasType]);

  // 历史成本趋势
  const { data: costTrendData, loading: trendLoading } = useRequest(
    async () => {
      const endDate = dayjs(dateValue?.[1]);
      const startDate = endDate.subtract(2, 'year').startOf('year');

      return await apiV2EnergyReportTrendPost({
        type: trendType,
        fromTime: startDate.valueOf(),
        toTime: endDate.endOf('year').valueOf(),
        timeResolution: 'DAY',
        deptOrArea: reportScope === 'dept' ? 1 : reportScope === 'area' ? 2 : undefined,
        deptAreaId: monthCycleApiParams?.deptAreaId,
        ...buildMonthCycleParams(DateType.DAY, monthCycleApiParams, reportScope),
      });
    },
    {
      ready: open && !!timeResolution && !!dateValue && !!dateValue?.[1],
      refreshDeps: [timeResolution, dateValue, open],
      debounceWait: 100,
    }
  );

  // 历史趋势图表
  const costTrendChartOption = useMemo(() => {
    const config = gasConfigsForChart[gasType];
    const title = config.modalLegendName ?? config?.title;
    return getGasCostTrendChartOption(costTrendData, title);
  }, [costTrendData]);

  return (
    <>
      <CardBox
        title={showTitle ? '用气详情' : undefined}
        extraContent={
          <div className={styles.time_box}>
            <Button type="primary" onClick={() => setOpen(true)}>
              历史成本趋势对比
            </Button>
          </div>
        }
      >
        <div className={styles.chart_wrapper}>
          <EchartsWithTheme
            empty={{
              isShowEmpty: !gasResult.data || gasResult.data?.list?.length === 0,
              emptyNode: <Empty />,
            }}
            loading={gasResult.loading}
            theme={theme}
            style={{ height: 300 }}
            option={chartOption}
          />
        </div>
      </CardBox>
      <Modal
        title="历史成本趋势对比"
        open={open}
        width="80%"
        mask={false}
        style={{ top: '10%', minWidth: 900 }}
        onOk={() => {}}
        footer={null}
        onCancel={() => {
          setOpen(false);
        }}
      >
        <div style={{ marginTop: 16, height: '70vh', width: '100%' }}>
          <EchartsWithTheme
            theme={theme}
            loading={trendLoading}
            empty={{
              isShowEmpty: !costTrendData || costTrendData.list?.length === 0,
              emptyNode: <Empty className={styles.empty_box} />,
            }}
            style={{ height: '100%', marginTop: 16 }}
            option={costTrendChartOption}
          />
        </div>
      </Modal>
    </>
  );
};

export default GasChart;
